// Client portal Packaging tab.
//
// Mirrors most of the admin packaging surface — clients can:
//   • see their package summary with run rates + balance
//   • see + change low-stock alerts (email auto-fills to the caller)
//   • define new packages, edit them, link them, delete them
//   • record stock receipts + delete typo receipts
//   • see who logged each receipt
//   • see the usage-trend line chart
//
// NOT exposed to clients (admin-only):
//   • Import from ShipStation
//   • Re-match orders against package codes

import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle, AlertTriangle, Edit2, ExternalLink, Loader2, Package, Plus,
  Ruler, Save, Search, Trash2, X,
} from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer,
} from 'recharts'
import { useAuth } from '../../../hooks/useAuth'
import {
  getMePackagingSummary, getMePackagingInventory, getMePackagingTypes,
  getMePackagingTimeseries,
  createMePackagingInventory, deleteMePackagingInventory,
  upsertMePackagingType, deleteMePackagingType,
} from '../../../lib/api'
import { ToastContainer, useToast } from '../../../components/Toast'
import { formatInt, formatDate } from './helpers'
import { businessToday } from '../../../lib/dates'

// ─── Small helpers ──────────────────────────────────────────────────────────

const OTHER_CODE = '__other__'
const TODAY = () => businessToday()
const LINE_COLOURS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899']

function fmtDim(v) {
  if (v == null) return ''
  return Number.isInteger(v) ? v : Number(v).toFixed(2).replace(/\.?0+$/, '')
}

function slugify(name) {
  return (name || '').trim().toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')
}

function pkgLabel(row) {
  return (row?.display_name && row.display_name.trim()) || row?.package_code || ''
}


// ─── Edit / Define metadata modal ───────────────────────────────────────────


function EditMetaModal({ clientId, row, onClose, isNew = false }) {
  // ShipStation-imported packages are locked to clients on name +
  // dimensions — those must stay in sync with the ShipStation record.
  // Manually-defined packages stay fully editable. The admin portal is
  // unaffected by this lock; it only kicks in on the client surface.
  const isShipstationLocked = !isNew && row.source === 'shipstation'

  const [displayName, setDisplayName] = useState(row.display_name || '')
  const [websiteName, setWebsiteName] = useState(row.website_name || '')
  const [reorderUrl,  setReorderUrl]  = useState(row.reorder_url || '')
  const [dimLength,   setDimLength]   = useState(row.dim_length ?? '')
  const [dimWidth,    setDimWidth]    = useState(row.dim_width  ?? '')
  const [dimHeight,   setDimHeight]   = useState(row.dim_height ?? '')
  const [dimUnits,    setDimUnits]    = useState(row.dim_units || 'centimeters')
  const [linkedToCodes,       setLinkedToCodes]       = useState(
    Array.isArray(row.linked_to_codes) && row.linked_to_codes.length > 0
      ? row.linked_to_codes.filter(Boolean)
      : (row.linked_to_code ? [row.linked_to_code] : [])
  )
  const [lowStockThreshold,   setLowStockThreshold]   = useState(row.low_stock_threshold ?? '')
  const [err, setErr] = useState(null)

  const { toasts, addToast, removeToast } = useToast()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const myEmail = user?.email || ''

  // For new packages: derive code from name. For existing: keep original.
  const packageCode = isNew ? slugify(displayName) : row.package_code

  // Link options: this client's other defined packages, excluding self and
  // anything that's already linked (one level of indirection only).
  const { data: existingTypes = [] } = useQuery({
    queryKey: ['me', 'packaging', 'types', clientId],
    queryFn:  () => getMePackagingTypes(clientId),
    enabled:  !!clientId,
  })
  const linkOptions = existingTypes.filter(t => {
    if (!t.package_code || t.package_code === row.package_code) return false
    // A target is "itself linked" if its array is non-empty OR (legacy) its
    // deprecated scalar is set. Prevents the UI from offering a two-level
    // chain that the backend will reject.
    const arrLinked = Array.isArray(t.linked_to_codes) && t.linked_to_codes.filter(Boolean).length > 0
    const scalarLinked = !!(t.linked_to_code && t.linked_to_code.trim())
    return !(arrLinked || scalarLinked)
  })

  const saveMut = useMutation({
    mutationFn: () => upsertMePackagingType(clientId, packageCode, {
      display_name:        displayName.trim() || null,
      website_name:        websiteName.trim() || null,
      reorder_url:         reorderUrl.trim() || null,
      dim_length:          dimLength === '' ? null : Number(dimLength),
      dim_width:           dimWidth  === '' ? null : Number(dimWidth),
      dim_height:          dimHeight === '' ? null : Number(dimHeight),
      dim_units:           dimUnits || null,
      linked_to_code:      linkedToCodes[0] || null,
      linked_to_codes:     linkedToCodes,
      low_stock_threshold: lowStockThreshold === '' ? null : parseInt(lowStockThreshold, 10),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'summary',     clientId] })
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'types',       clientId] })
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'inventory',   clientId] })
      onClose()
    },
    onError: (e) => setErr(e.response?.data?.detail || e.message || 'Save failed.'),
  })

  const deleteMut = useMutation({
    mutationFn: () => deleteMePackagingType(clientId, row.package_code),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'summary',   clientId] })
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'types',     clientId] })
      onClose()
    },
    onError: (e) => setErr(e.response?.data?.detail || e.message || 'Delete failed.'),
  })

  function handleSubmit(e) {
    e.preventDefault()
    setErr(null)
    if (!displayName.trim()) return setErr('Package name is required.')
    if (isNew && !packageCode) return setErr('Name must contain at least one letter or number.')
    saveMut.mutate()
  }

  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
        <div>
          <h3 className="text-base font-semibold text-slate-800">
            {isNew ? 'Define new package' : 'Edit package'}
          </h3>
          {!isNew && row.package_code && (
            <p className="text-xs text-slate-300 mt-0.5 font-mono">{row.package_code}</p>
          )}
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
      </div>

      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        {isShipstationLocked && (
          <div className="text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded px-3 py-2 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0 text-blue-600" />
            <span>
              Imported from ShipStation. The package <b>name</b> and <b>dimensions</b> are managed
              in ShipStation — you can still edit website name, reorder URL, linking, and the
              low-stock alert here.
            </span>
          </div>
        )}

        <Field label="Package name" required>
          <input type="text" value={displayName} onChange={e => setDisplayName(e.target.value)}
                 className="input"
                 placeholder="e.g. ATG - Belt Satchel"
                 disabled={isShipstationLocked}
                 readOnly={isShipstationLocked}
                 autoFocus={!isShipstationLocked} required />
          <p className="text-xs text-slate-400 mt-1">
            {isShipstationLocked
              ? 'Locked — change in ShipStation, then ask your account manager to re-import.'
              : 'How this package shows up in the portal.'}
          </p>
        </Field>

        <Field label="Website name">
          <input type="text" value={websiteName} onChange={e => setWebsiteName(e.target.value)}
                 className="input" placeholder="e.g. Mailing Box Large 250 x 175 x 95mm" />
          <p className="text-xs text-slate-400 mt-1">
            The exact name on the supplier's website — useful for reorders.
          </p>
        </Field>

        <Field label={
          <span className="flex items-center gap-1.5 text-slate-500">
            <Ruler size={12} /> Dimensions
            <span className="font-normal text-slate-400 lowercase">
              {isShipstationLocked ? '(managed in ShipStation)' : '(used to auto-tag matching orders)'}
            </span>
          </span>
        }>
          <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
            <input type="number" min="0" step="0.01" value={dimLength}
                   onChange={e => setDimLength(e.target.value)}
                   className="input text-sm" placeholder="L"
                   disabled={isShipstationLocked} readOnly={isShipstationLocked} />
            <input type="number" min="0" step="0.01" value={dimWidth}
                   onChange={e => setDimWidth(e.target.value)}
                   className="input text-sm" placeholder="W"
                   disabled={isShipstationLocked} readOnly={isShipstationLocked} />
            <input type="number" min="0" step="0.01" value={dimHeight}
                   onChange={e => setDimHeight(e.target.value)}
                   className="input text-sm" placeholder="H"
                   disabled={isShipstationLocked} readOnly={isShipstationLocked} />
            <select value={dimUnits} onChange={e => setDimUnits(e.target.value)}
                    className="input text-sm w-20"
                    disabled={isShipstationLocked}>
              <option value="inches">in</option>
              <option value="centimeters">cm</option>
              <option value="millimeters">mm</option>
            </select>
          </div>
        </Field>

        <Field label="Reorder URL">
          <input type="url" value={reorderUrl} onChange={e => setReorderUrl(e.target.value)}
                 className="input" placeholder="https://supplier.example.com/product" />
        </Field>

        {/* Linking */}
        <Field label="Depletes when another package is used">
          {linkOptions.length === 0 ? (
            <div className="text-xs text-slate-500 border border-slate-200 rounded px-3 py-2 bg-slate-50">
              — Stand-alone (no triggers) —
            </div>
          ) : (
            <div className="border border-slate-200 rounded max-h-[180px] overflow-y-auto">
              {linkedToCodes.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-400 italic border-b border-slate-100">
                  — Stand-alone (no triggers) —
                </div>
              )}
              {linkOptions.map(opt => {
                const checked = linkedToCodes.includes(opt.package_code)
                return (
                  <label key={opt.package_code}
                         className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0">
                    <input type="checkbox"
                           checked={checked}
                           onChange={e => {
                             if (e.target.checked) {
                               setLinkedToCodes([...linkedToCodes, opt.package_code])
                             } else {
                               setLinkedToCodes(linkedToCodes.filter(c => c !== opt.package_code))
                             }
                           }} />
                    <span className="text-slate-700">{opt.display_name || opt.package_code}</span>
                  </label>
                )
              })}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-1">
            Optional. Pick one or more parent packages whose usage drives this one —
            this package depletes once for EACH order that uses ANY of the checked
            triggers. Inventory stays separate.
          </p>
        </Field>

        {/* Low stock alert */}
        <div className="pt-2 border-t border-slate-100">
          <label className="label flex items-center gap-1.5 text-slate-500">
            <AlertCircle size={12} /> Low-stock alert
            <span className="font-normal text-slate-400 lowercase">(optional)</span>
          </label>
          <div className="flex items-center gap-2">
            <input type="number" min="0" step="1" value={lowStockThreshold}
                   onChange={e => setLowStockThreshold(e.target.value)}
                   className="input text-sm w-28" placeholder="Qty" />
            <span className="text-xs text-slate-500">
              Emails {myEmail ? <b className="text-slate-700">{myEmail}</b> : 'you'} when balance ≤ threshold
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-1">
            Fires once per dip; re-arms when the balance climbs back above the threshold. Clear the qty to disable.
          </p>
        </div>

        {err && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded flex items-start gap-2">
            <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
            <span>{err}</span>
          </div>
        )}

        <div className="flex justify-between items-center gap-2 pt-2 border-t border-slate-100">
          {!isNew ? (
            <button type="button"
                    onClick={() => {
                      if (window.confirm(`Delete package "${pkgLabel(row)}"?`)) {
                        deleteMut.mutate()
                      }
                    }}
                    disabled={deleteMut.isPending}
                    className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1">
              {deleteMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
              Delete
            </button>
          ) : <div />}
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
                    className="px-3 py-1.5 text-sm rounded border border-slate-300 hover:bg-slate-50">
              Cancel
            </button>
            <button type="submit" disabled={saveMut.isPending}
                    className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 inline-flex items-center gap-1.5">
              {saveMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          </div>
        </div>
      </form>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ModalShell>
  )
}


// ─── Inventory section (Record New Stock + table) ───────────────────────────


function InventorySection({ clientId, packageCodes, codeToLabel = {} }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  // Drop the synthetic Other bucket; can't log receipts against it.
  const selectableCodes = packageCodes.filter(c => c !== OTHER_CODE)

  const [packageCode, setPackageCode]   = useState('')
  const [quantity, setQuantity]         = useState('')
  const [receivedDate, setReceivedDate] = useState(TODAY())
  const [notes, setNotes]               = useState('')

  const {
    data: entries = [], isLoading,
    isError, error,
  } = useQuery({
    queryKey: ['me', 'packaging', 'inventory', clientId],
    queryFn:  () => getMePackagingInventory(clientId),
    enabled:  !!clientId,
  })

  const createMut = useMutation({
    mutationFn: (data) => createMePackagingInventory(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'inventory', clientId] })
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'summary',   clientId] })
      setPackageCode(''); setQuantity(''); setNotes('')
      addToast({ type: 'success', message: 'Receipt recorded' })
    },
    onError: (e) => addToast({ type: 'error', message: e.response?.data?.detail || 'Failed to add receipt' }),
  })

  const deleteMut = useMutation({
    mutationFn: (id) => deleteMePackagingInventory(clientId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'inventory', clientId] })
      queryClient.invalidateQueries({ queryKey: ['me', 'packaging', 'summary',   clientId] })
      addToast({ type: 'success', message: 'Receipt removed' })
    },
    onError: (e) => addToast({ type: 'error', message: e.response?.data?.detail || 'Failed to delete' }),
  })

  function handleAdd(e) {
    e.preventDefault()
    const code = packageCode
    const qty = parseInt(quantity, 10)
    if (!code) { addToast({ type: 'error', message: 'Choose a package' }); return }
    if (!qty || qty <= 0) { addToast({ type: 'error', message: 'Quantity must be > 0' }); return }
    createMut.mutate({
      package_code: code,
      quantity: qty,
      received_date: receivedDate || null,
      notes: notes.trim() || null,
    })
  }

  return (
    <div className="card overflow-hidden">
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-700">Record new stock received</h3>
        <p className="text-xs text-slate-400 mt-0.5">
          Add an entry whenever new packaging arrives so the inventory balance stays accurate.
        </p>
      </div>

      <form onSubmit={handleAdd} className="px-4 py-3 border-b border-slate-200 grid grid-cols-[1.4fr_0.7fr_1fr_1.4fr_auto] gap-3 items-end">
        <Field label="Package">
          <select value={packageCode} onChange={e => setPackageCode(e.target.value)} className="input">
            <option value="">Select…</option>
            {selectableCodes.map(c => (
              <option key={c} value={c}>{codeToLabel[c] || c}</option>
            ))}
          </select>
        </Field>
        <Field label="Quantity">
          <input type="number" min="1" step="1" value={quantity}
                 onChange={e => setQuantity(e.target.value)} className="input" placeholder="0" />
        </Field>
        <Field label="Received">
          <input type="date" value={receivedDate}
                 onChange={e => setReceivedDate(e.target.value)} className="input" />
        </Field>
        <Field label="Notes">
          <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
                 className="input" placeholder="PO #, supplier, etc." />
        </Field>
        <button type="submit" disabled={createMut.isPending}
                className="btn-primary text-sm inline-flex items-center gap-1.5">
          {createMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add
        </button>
      </form>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : isError ? (
        <div className="p-6 text-sm text-red-700 bg-red-50 border-t border-red-200 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <span>Couldn't load receipts. {error?.response?.data?.detail || error?.message || 'Try again.'}</span>
        </div>
      ) : entries.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-400">No receipts recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-2.5">Received</th>
                <th className="px-4 py-2.5">Package</th>
                <th className="px-4 py-2.5 text-right">Qty</th>
                <th className="px-4 py-2.5">Notes</th>
                <th className="px-4 py-2.5">Added by</th>
                <th className="px-4 py-2.5 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e, i) => (
                <tr key={e.id || i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                  <td className="px-4 py-2 text-slate-600">{formatDate(e.received_date)}</td>
                  <td className="px-4 py-2 text-slate-700">
                    {codeToLabel[e.package_code] || e.package_code}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums text-slate-700">{formatInt(e.quantity)}</td>
                  <td className="px-4 py-2 text-slate-500 max-w-xs truncate" title={e.notes || ''}>{e.notes || '—'}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">
                    {e.created_by_name || e.created_by_email || <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <button onClick={() => {
                              if (window.confirm('Remove this receipt? The balance will update accordingly.')) {
                                deleteMut.mutate(e.id)
                              }
                            }}
                            className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"
                            title="Remove">
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}


// ─── Usage trend chart ──────────────────────────────────────────────────────


function UsageTrendChart({ clientId, codeToLabel = {} }) {
  const [filter, setFilter] = useState('all')

  const { data: series = [], isLoading, error: seriesError } = useQuery({
    queryKey: ['me', 'packaging', 'timeseries', clientId, filter],
    queryFn:  () => getMePackagingTimeseries(clientId, filter !== 'all' ? { package_code: filter } : {}),
    enabled:  !!clientId,
  })

  // Pivot {date, code, count} into a list of {date, code1: n, code2: n, ...}
  // plus a 7-day moving average for each code.
  const MA_WINDOW = 7
  const { chartData, packageList } = useMemo(() => {
    if (series.length === 0) return { chartData: [], packageList: [] }
    const codes = Array.from(new Set(series.map(p => p.package_code)))
    const byDate = new Map()
    for (const p of series) {
      const d = String(p.date).slice(0, 10)
      const row = byDate.get(d) || { date: d }
      row[p.package_code] = (row[p.package_code] || 0) + (p.count || 0)
      byDate.set(d, row)
    }
    const data = Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date))
    // Trailing moving average per code
    for (const code of codes) {
      const window = []
      for (const row of data) {
        const v = row[code] || 0
        window.push(v)
        if (window.length > MA_WINDOW) window.shift()
        row[`${code}__ma`] = window.reduce((s, x) => s + x, 0) / window.length
      }
    }
    return { chartData: data, packageList: codes }
  }, [series])

  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-700">Usage trend</h3>
          <p className="text-xs text-slate-400 mt-0.5">
            Daily orders by package (last 8 weeks). Dashed lines show the 7-day rolling average.
          </p>
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
                className="text-xs border border-slate-200 rounded px-2 py-1 text-slate-600 focus:outline-none focus:ring-1 focus:ring-blue-400">
          <option value="all">All packages</option>
          {packageList.filter(c => c !== OTHER_CODE).map(c => (
            <option key={c} value={c}>{codeToLabel[c] || c}</option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : seriesError ? (
        /* `series` defaults to [] on failure, so the "no usage" state below
           would report a broken request as zero usage. */
        <div className="p-6 text-sm text-red-700 bg-red-50 border-t border-red-200 flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium">Couldn't load the usage trend.</div>
            <div className="text-xs text-red-600 mt-0.5">
              This is <b>not</b> zero usage — the request failed.{' '}
              {seriesError?.response?.data?.detail || seriesError?.message || 'Unknown error'}
            </div>
          </div>
        </div>
      ) : chartData.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400">No usage in the last 8 weeks.</div>
      ) : (
        <div className="px-2 py-3">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748b' }}
                     tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }}
                formatter={(v) => Math.round(v)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {packageList.map((c, i) => (
                <Line
                  key={c}
                  type="monotone"
                  dataKey={c}
                  name={codeToLabel[c] || c}
                  stroke={LINE_COLOURS[i % LINE_COLOURS.length]}
                  strokeWidth={1.5}
                  dot={false}
                />
              ))}
              {packageList.map((c, i) => (
                <Line
                  key={`${c}__ma`}
                  type="monotone"
                  dataKey={`${c}__ma`}
                  name={`${codeToLabel[c] || c} (7-day avg)`}
                  stroke={LINE_COLOURS[i % LINE_COLOURS.length]}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  dot={false}
                  legendType="none"
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}


// ─── Main tab ───────────────────────────────────────────────────────────────


export default function PackagingTab({ clientId }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(null)   // a summary row, or null
  const [adding, setAdding]   = useState(false)

  const {
    data: summary = [], isLoading: summaryLoading,
    isError: summaryError, error: summaryErrorObj,
  } = useQuery({
    queryKey: ['me', 'packaging', 'summary', clientId],
    queryFn:  () => getMePackagingSummary(clientId),
    enabled:  !!clientId,
    retry: 1,
  })

  // Full package_types list so the alias-chip click can find the row.
  const { data: allTypes = [] } = useQuery({
    queryKey: ['me', 'packaging', 'types', clientId],
    queryFn:  () => getMePackagingTypes(clientId),
    enabled:  !!clientId,
  })
  const typesByCode = useMemo(() => {
    const out = {}
    for (const t of allTypes) if (t.package_code) out[t.package_code] = t
    return out
  }, [allTypes])

  // For the InventorySection's dropdown + the chart's labels
  const packageCodes = useMemo(() => summary.filter(r => r.package_code !== OTHER_CODE).map(r => r.package_code), [summary])
  const codeToLabel  = useMemo(() => {
    const m = {}
    for (const r of summary) m[r.package_code] = pkgLabel(r)
    return m
  }, [summary])

  return (
    <div className="space-y-4">
      {/* Summary table */}
      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-700">Package summary</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Current stock balance and recent-week run rates per package.
            </p>
          </div>
          <button onClick={() => setAdding(true)}
                  className="btn-primary text-sm inline-flex items-center gap-1.5">
            <Plus size={14} /> Define new package
          </button>
        </div>

        {summaryLoading ? (
          <div className="flex items-center justify-center py-12 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : summaryError ? (
          <div className="p-6 text-sm text-red-700 bg-red-50 border-t border-red-200 flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">Couldn't load packaging data.</div>
              <div className="text-xs text-red-600 mt-0.5">
                {summaryErrorObj?.response?.data?.detail || summaryErrorObj?.message || 'Unknown error'}
              </div>
            </div>
          </div>
        ) : summary.length === 0 ? (
          <div className="p-8 text-center">
            <Package size={20} className="inline-block mb-2 text-slate-300" />
            <p className="text-sm text-slate-400">No packages yet.</p>
            <p className="text-xs text-slate-400 mt-1">
              Click <b>Define new package</b> above to add your first one.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <th className="px-4 py-2.5 text-left">Package</th>
                  <th className="px-4 py-2.5 text-right">Balance</th>
                  <th className="px-4 py-2.5 text-right">1W / wk</th>
                  <th className="px-4 py-2.5 text-right">2W / wk</th>
                  <th className="px-4 py-2.5 text-right">4W / wk</th>
                  <th className="px-4 py-2.5 text-right">8W / wk</th>
                  <th className="px-4 py-2.5 text-center"></th>
                  <th className="px-4 py-2.5 w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {summary.map((row, i) => {
                  const isOther = row.package_code === OTHER_CODE
                  const label   = pkgLabel(row)
                  const dimsLabel = row.dim_length && row.dim_width && row.dim_height
                    ? `${fmtDim(row.dim_length)} × ${fmtDim(row.dim_width)} × ${fmtDim(row.dim_height)} ${row.dim_units || ''}`
                    : null
                  const hasThreshold = !isOther && typeof row.low_stock_threshold === 'number'
                  const isLow        = hasThreshold && row.balance <= row.low_stock_threshold
                  return (
                    <tr key={row.package_code}
                        className={`${isOther ? 'bg-slate-50/40' : isLow ? 'bg-red-50/30' : i % 2 === 1 ? 'bg-slate-50/40' : ''}`}>
                      <td className="px-4 py-2">
                        <div className={`font-medium ${isOther ? 'text-slate-500 italic' : 'text-slate-800'}`}>{label}</div>
                        {row.website_name && (
                          <div className="text-xs text-slate-500 mt-0.5">{row.website_name}</div>
                        )}
                        {dimsLabel && (
                          <div className="text-xs text-slate-400 mt-0.5 tabular-nums">{dimsLabel}</div>
                        )}
                        {hasThreshold && (
                          <div className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1">
                            <AlertCircle size={10} /> Alert at ≤ {row.low_stock_threshold}
                            {row.low_stock_email && (
                              <span className="text-slate-300">· {row.low_stock_email}</span>
                            )}
                          </div>
                        )}
                        {(() => {
                          // Show every trigger, not just the first. Legacy rows
                          // still on the scalar fall back to a single-element list.
                          const triggers = (Array.isArray(row.linked_to_codes) && row.linked_to_codes.length > 0)
                            ? row.linked_to_codes.filter(Boolean)
                            : (row.linked_to_code ? [row.linked_to_code] : [])
                          if (triggers.length === 0) return null
                          return (
                            <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-1">
                              <span>↗ Depletes with</span>
                              {triggers.map((tc, i) => (
                                <span key={tc} className="inline-flex items-center">
                                  <button type="button"
                                          onClick={() => {
                                            const full = typesByCode[tc]
                                            setEditing(full || { package_code: tc, display_name: tc })
                                          }}
                                          className="text-blue-500 hover:text-blue-700 hover:underline">
                                    {typesByCode[tc]?.display_name || tc}
                                  </button>
                                  {i < triggers.length - 1 && <span className="ml-0.5">,</span>}
                                </span>
                              ))}
                            </div>
                          )
                        })()}
                        {row.linked_aliases?.length > 0 && (
                          <div className="text-xs text-slate-400 mt-0.5 flex flex-wrap items-center gap-1">
                            <span>↗ Also depletes</span>
                            {row.linked_aliases.map((dep, i) => (
                              <span key={dep.package_code} className="inline-flex items-center">
                                <button type="button"
                                        onClick={() => {
                                          const full = typesByCode[dep.package_code]
                                          setEditing(full || { package_code: dep.package_code, display_name: dep.display_name })
                                        }}
                                        className="text-blue-500 hover:text-blue-700 hover:underline">
                                  {dep.display_name}
                                </button>
                                {i < row.linked_aliases.length - 1 && <span className="ml-0.5">,</span>}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className={`px-4 py-2 text-right tabular-nums font-semibold ${
                        isOther ? 'text-slate-300' :
                        row.balance < 0 ? 'text-red-600' :
                        isLow ? 'text-red-600' :
                        row.balance < 50 ? 'text-amber-600' : 'text-slate-700'
                      }`}>
                        {isOther ? '—' : (
                          <span className="inline-flex items-center justify-end gap-1.5">
                            {(row.balance < 0 || isLow) && <AlertCircle size={11} />}
                            {formatInt(row.balance)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{row.run_rate_1w?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{row.run_rate_2w?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{row.run_rate_4w?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-slate-500">{row.run_rate_8w?.toFixed(1) ?? '—'}</td>
                      <td className="px-4 py-2 text-center">
                        {row.reorder_url ? (
                          <a href={row.reorder_url} target="_blank" rel="noopener noreferrer"
                             className="text-blue-600 hover:text-blue-700 inline-flex items-center"
                             title="Open reorder link">
                            <ExternalLink size={14} />
                          </a>
                        ) : <span className="text-slate-200">—</span>}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {!isOther && (
                          <button onClick={() => setEditing(row)}
                                  className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                                  title="Edit">
                            <Edit2 size={13} />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Usage trend chart */}
      <UsageTrendChart clientId={clientId} codeToLabel={codeToLabel} />

      {/* Inventory form + table */}
      <InventorySection clientId={clientId} packageCodes={packageCodes} codeToLabel={codeToLabel} />

      {/* Modals */}
      {editing && (
        <EditMetaModal
          clientId={clientId}
          row={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {adding && (
        <EditMetaModal
          clientId={clientId}
          row={{ package_code: '', display_name: '', reorder_url: '', dim_units: 'centimeters' }}
          isNew
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  )
}


// ─── Small primitives ───────────────────────────────────────────────────────


function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
    </div>
  )
}

function ModalShell({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" onClick={onClose}>
      <div className="flex min-h-full items-start justify-center p-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
             onClick={e => e.stopPropagation()}>
          {children}
        </div>
      </div>
    </div>
  )
}
